
import { supabase } from "@/integrations/supabase/client";
import { NotificationData } from '../types/notifications';
import { validateTemplateType } from '../email-templates';
import { updateLogStatus } from './notificationLogger';

export const sendNotificationToRecipient = async (
  type: string,
  email: string,
  data: NotificationData,
  logId: string
) => {
  try {
    console.log(`Attempting to send ${type} email to ${email}`);
    
    if (!validateTemplateType(type)) {
      throw new Error(`Invalid template type: ${type}`);
    }

    // Ensure required fields are present
    const templateData = {
      recipientName: data.recipientName || 'User',
      ...data
    };

    console.log(`Sending notification via send-notification edge function with type: ${type}`);
    
    // Prepare headers to include origin information for environment-aware URLs
    const headers: Record<string, string> = {};
    
    // Include current origin if available
    if (typeof window !== 'undefined') {
      headers['origin'] = window.location.origin;
      headers['referer'] = window.location.href;
    }
    
    // Send the notification using the centralized send-notification function
    const response = await supabase.functions.invoke('send-notification', {
      body: {
        type,
        recipientEmail: email,
        templateProps: templateData
      },
      headers
    });
    
    if (response.error) {
      throw new Error(`Send-notification function error: ${response.error.message || 'Unknown error'}`);
    }
    
    if (!response.data) {
      throw new Error('No data returned from send-notification function');
    }
    
    console.log(`Email sent successfully to ${email} with type ${type}`, response.data);
    await updateLogStatus(logId, true);
    
    return { success: true, email, logId: response.data.email_log_id };
  } catch (sendError: any) {
    console.error(`Error sending email to ${email}:`, sendError);
    await updateLogStatus(logId, false, sendError.message);
    return { success: false, email, error: sendError.message };
  }
};

// Enhanced direct email sending function for welcome emails
export const sendWelcomeEmailDirect = async (email: string, name: string) => {
  try {
    console.log(`[Direct Email] Sending welcome email to new Google user: ${email}`);
    
    // Check if a welcome email was already sent in the last hour
    const { data: recentEmails } = await supabase
      .from('email_logs')
      .select('*')
      .eq('email_type', 'authSignup')
      .eq('recipient_email', email)
      .order('sent_at', { ascending: false })
      .limit(1);
    
    const oneHourAgo = new Date();
    oneHourAgo.setHours(oneHourAgo.getHours() - 1);
    
    if (recentEmails && recentEmails.length > 0) {
      const lastEmailSent = new Date(recentEmails[0].sent_at);
      if (lastEmailSent > oneHourAgo) {
        console.log(`[Direct Email] Welcome email already sent recently to ${email}, skipping duplicate`);
        return { success: true, skipped: true };
      }
    }
    
    console.log(`[Direct Email] Sending welcome email via send-notification edge function`);
    
    // Prepare headers to include origin information for environment-aware URLs
    const headers: Record<string, string> = {};
    
    // Include current origin if available
    if (typeof window !== 'undefined') {
      headers['origin'] = window.location.origin;
      headers['referer'] = window.location.href;
    }
    
    // Send the welcome email using the centralized send-notification function
    const response = await supabase.functions.invoke('send-notification', {
      body: {
        type: 'authSignup',
        recipientEmail: email,
        templateProps: {
          recipientName: name || 'User',
          credentials: { email }
        }
      },
      headers
    });
    
    if (response.error) {
      throw new Error(`Send-notification function error: ${response.error.message || 'Unknown error'}`);
    }
    
    console.log(`[Direct Email] Welcome email sent successfully to ${email}`, response.data);
    return { success: true, logId: response.data?.email_log_id };
  } catch (error: any) {
    console.error(`[Direct Email] Failed to send welcome email:`, error);
    return { success: false, error: error.message };
  }
};
